/**
 * Auth Service - Authentication operations
 * Pure service with no React dependencies
 * Uses TokenManager for token lifecycle management
 */

import { get, post, patch } from "../api";
import { AUTH_ENDPOINTS } from "../api/constants";
import { tokenManager } from "./tokenManager";
import { translate } from "../../hooks";

/**
 * Authentication Service
 * Provides login, logout, and user management operations
 */
export const authService = {
  /**
   * Login user with email and password
   * Handles both regular login and 2FA flow
   *
   * @param {string} email - User email or identifier
   * @param {string} password - User password
   * @returns {Promise<Object>} Login result object
   *   - On success: {token, user, twoFactorEnabled: false}
   *   - On 2FA required: {twoFactorEnabled: true, email}
   *   - On error: {success: false, error: string}
   */
  async login(email, password) {
    try {
      const data = await post(AUTH_ENDPOINTS.LOGIN, { identifier: email, password });

      if (data.jwt) {
        // Regular login - save token
        tokenManager.setToken(data.jwt, 30);
        return {
          success: true,
          token: data.jwt,
          user: data.user,
          twoFactorEnabled: false,
        };
      }

      if (data.requiresTwoFactor) {
        // 2FA required
        return {
          success: true,
          twoFactorEnabled: true,
          email,
        };
      }

      return {
        success: false,
        error: data?.error?.message || translate("ERRORS.LOGIN_FAILED"),
      };
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        error: error.message || translate("ERRORS.LOGIN_FAILED"),
      };
    }
  },

  /**
   * Logout current user
   * Clears local token and calls backend logout endpoint
   *
   * @returns {Promise<boolean>} Success status
   *
   * @example
   * await authService.logout();
   */
  async logout() {
    // Get current token before clearing
    const token = tokenManager.getValidToken();

    // Clear token first
    tokenManager.clearToken();

    // Try to notify backend if we had a valid token
    if (token) {
      try {
        await post(AUTH_ENDPOINTS.LOGOUT, {}, token);
      } catch (error) {
        // Token already expired or invalid - ignore error
        console.debug("Logout endpoint call failed (token may be expired):", error.message);
      }
    }

    return true;
  },

  /**
   * Get current logged-in user profile
   * Fetches user data from backend
   *
   * @returns {Promise<Object|null>} User profile object or null if not authenticated
   *   Returns: {id, username, email, userPrivileges, operator}
   */
  async getCurrentUser() {
    const token = tokenManager.getValidToken();
    if (!token) {
      return null;
    }

    try {
      const data = await get(AUTH_ENDPOINTS.CURRENT_USER, token);

      return {
        documentId: data.user?.documentId || null,
        id: data.user?.documentId || data.user?.id || null,
        username: data.user?.username || "User",
        email: data.user?.email || "",
        userPrivileges: data.user?.userPrivileges || "Technician",
        operator: data.user?.operator_ref?.name || data.user?.operator_ref?.documentId || null,
        operator_ref: data.user?.operator_ref || null,
      };
    } catch (error) {
      console.error("Error fetching current user:", error);
      return null;
    }
  },

  /**
   * Verify 2FA code and complete login
   * Called after user enters 2FA code
   *
   * @param {string} identifier - User email or identifier
   * @param {string} code - 2FA code (4-6 digits)
   * @returns {Promise<Object>} Verification result
   *   - On success: {jwt, user}
   *   - On error: {error: {message: string}}
   *
   * @throws {Error} If verification fails
   */
  async verify2FA(identifier, code) {
    try {
      const data = await post(AUTH_ENDPOINTS.VERIFY_2FA, { identifier, code });

      if (!data.jwt) {
        throw new Error(data?.error?.message || translate("ERRORS.INVALID_2FA_CODE"));
      }

      // Save token on successful 2FA verification
      tokenManager.setToken(data.jwt, 30);

      return data;
    } catch (error) {
      console.error("2FA verification error:", error);
      throw error;
    }
  },

  /**
   * Resend 2FA code to user
   * Called if user didn't receive the code
   *
   * @param {string} identifier - User email or identifier
   * @returns {Promise<Object>} Resend result
   *
   * @throws {Error} If resend fails
   */
  async resend2FA(identifier) {
    try {
      const data = await post(AUTH_ENDPOINTS.RESEND_2FA, { identifier });

      if (data?.error) {
        throw new Error(data.error.message || translate("ERRORS.RESEND_2FA_FAILED"));
      }

      return data;
    } catch (error) {
      console.error("Resend 2FA error:", error);
      throw error;
    }
  },

  /**
   * Enable 2FA for current user
   * Requires valid authentication token
   *
   * @returns {Promise<Object>} 2FA enablement result
   *
   * @throws {Error} If user not authenticated or operation fails
   */
  async enable2FA() {
    const token = tokenManager.getValidToken();
    if (!token) {
      throw new Error(translate("ERRORS.NOT_AUTHENTICATED"));
    }

    try {
      return await patch(AUTH_ENDPOINTS.ENABLE_2FA, {}, token);
    } catch (error) {
      console.error("Enable 2FA error:", error);
      throw error;
    }
  },

  /**
   * Disable 2FA for current user
   * Requires valid authentication token
   *
   * @returns {Promise<Object>} 2FA disablement result
   *
   * @throws {Error} If user not authenticated or operation fails
   */
  async disable2FA() {
    const token = tokenManager.getValidToken();
    if (!token) {
      throw new Error(translate("ERRORS.NOT_AUTHENTICATED"));
    }

    try {
      return await patch(AUTH_ENDPOINTS.DISABLE_2FA, {}, token);
    } catch (error) {
      console.error("Disable 2FA error:", error);
      throw error;
    }
  },
};

export default authService;
